import React from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';

import Honeybadger from '@honeybadger-io/js';
import ChatWindow from './ChatWindow';

export const MESSAGE_ATTRIBUTES = gql`
  fragment MessageAttributes on Message {
    textBody
    responses
    sender
    timeStamp
    errorMessage
    dialogflowIntentDisplayName

    __typename
  }
`;

export const GET_CHAT = gql`
  query chat($id: ID!) {
    chat(id: $id) {
      id
      messages {
        ...MessageAttributes
      }
    }
  }
  ${MESSAGE_ATTRIBUTES}
`;

export const SEND_MESSAGE = gql`
  mutation sendMessage($id: ID!, $textBody: String!) {
    sendMessage(id: $id, textBody: $textBody) {
      textBody
    }
  }
`;

export const MESSAGE_SUBSCRIPTION = gql`
  subscription ($id: ID!) {
    subscribeMessages(id: $id) {
      ...MessageAttributes
    }
  }
  ${MESSAGE_ATTRIBUTES}
`;

export interface ChatWindowContainerProps {
  chatId: string;
  open: boolean;
  isMinimizing: boolean;
  collapsible: boolean;
  onClickMinimize: () => void;
}

function ChatWindowContainer(props: ChatWindowContainerProps): JSX.Element {
  const { chatId, open, isMinimizing, collapsible, onClickMinimize } = props;
  const [sendMessage] = useMutation(SEND_MESSAGE, {
    onError: (e) => Honeybadger.notify(e, 'Error from Apollo useMutation in ChatWindowContainer.'),
  });
  const { subscribeToMore, ...result } = useQuery(GET_CHAT, {
    variables: { id: chatId },
  });

  const { loading, error, data } = result;

  if (loading) return <p>Loading ...</p>;
  if (error) return <p>Error ...</p>;

  const {
    chat: { messages },
  } = data;

  return (
    <ChatWindow
      chatId={chatId}
      open={open}
      messages={messages}
      onClickMinimize={onClickMinimize}
      isMinimizing={isMinimizing}
      collapsible={collapsible}
      sendMessage={sendMessage}
      subscribeToNewMessages={(): (() => void) => {
        return subscribeToMore({
          document: MESSAGE_SUBSCRIPTION,
          variables: { id: chatId },
          updateQuery: (previousResult, { subscriptionData }) => {
            const { messages: previousMessages } = previousResult.chat;
            // Updates props with new messages
            const newMessages = previousMessages.concat([subscriptionData.data.subscribeMessages]);
            const newResult = {
              chat: {
                id: previousResult.chat.id,
                messages: newMessages,
                __typename: 'Chat',
              },
            };
            return newResult;
          },
        });
      }}
    />
  );
}

export default ChatWindowContainer;
